import { DATE_FORMAT } from '@/constants/common.constant'
import { AxiomReportType } from '@/enums/axiom-report.enum'
import { useAppSelector } from '@/hooks/use-app-selector'
import { IGfpaSummaryDTO } from '@/interfaces/gfpaReport.interface'
import { getColumnDetail } from '@/utils/grid-utils/columnUtils'
import { formatCellValue } from '@/utils/tableGridUtils'
import { formatNumber } from '@/utils/utils'
import dayjs from 'dayjs'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import React, { useEffect, useState } from 'react'

export type IGFPASummary = {
  chartData: IGfpaSummaryDTO[]
}

export const GFPASummaryChart: React.FunctionComponent<IGFPASummary> = ({
  chartData,
}) => {
  const layout = useAppSelector((s) => s.verticallySplitLayout?.contentFocus)
  const [chartOptions, setChartOptions] = useState({
    chart: {
      height: 360,
    },
    title: {
      text: null,
    },
    xAxis: {},
    yAxis: [
      {
        title: {
          text: 'Return %',
        },
      },
    ],
    legend: {
      align: 'right',
      verticalAlign: 'center',
      layout: 'horizontal',
      y: 0,
    },
    series: [],
    credits: {
      enabled: false,
    },
    plotOptions: {
      series: {
        keys: ['y', 'custom.value'],
      },
    },
    tooltip: {
      headerFormat: '<p>Date: {point.x}</p><br/>',
      pointFormat:
        '<p>{series.name}: <strong>{point.options.custom.value}</strong></p>',
    },
  })

  useEffect(() => {
    const portFolioData = {
      name: getColumnDetail('p', AxiomReportType.GFPA).header,
      data: [] as [number, string][],
      marker: {
        enabled: false,
      },
    }
    const benchMarkData = {
      name: getColumnDetail('b', AxiomReportType.GFPA).header,
      data: [] as [number, string][],
      marker: {
        enabled: false,
      },
    }
    const activeData = {
      name: getColumnDetail('d', AxiomReportType.GFPA).header,
      data: [] as [number, string][],
      marker: {
        enabled: false,
      },
    }
    const categories: string[] = []

    function convertCategories(categoryList: any[]) {
      const dataPointsThreshold = 25
      if (categoryList.length > dataPointsThreshold) {
        return categoryList.map((date) => {
          const month = dayjs(date).format('MMM YYYY')
          return month
        })
      } else {
        return categoryList
      }
    }

    chartData &&
      chartData.length &&
      chartData.forEach((data: IGfpaSummaryDTO) => {
        portFolioData.data.push([
          Number(formatNumber(data.p)),
          formatCellValue(
            formatNumber(data.p),
            getColumnDetail('p', AxiomReportType.GFPA).format,
          ),
        ])
        benchMarkData.data.push([
          Number(formatNumber(data.b)),
          formatCellValue(
            formatNumber(data.b),
            getColumnDetail('b', AxiomReportType.GFPA).format,
          ),
        ])
        activeData.data.push([
          Number(formatNumber(data.d)),
          formatCellValue(
            formatNumber(data.d),
            getColumnDetail('d', AxiomReportType.GFPA).format,
          ),
        ])
        categories.push(dayjs(data.date_col).format(DATE_FORMAT.TABLE_CHART))
      })

    portFolioData.marker.enabled = portFolioData.data.length < 5
    benchMarkData.marker.enabled = benchMarkData.data.length < 5
    activeData.marker.enabled = activeData.data.length < 5

    const updatedChartOptions: any = { ...chartOptions }
    updatedChartOptions.xAxis = {
      tickInterval: categories.length <= 25 ? 1 : 25,
      categories: convertCategories(categories),
    }
    updatedChartOptions.series[0] = portFolioData
    updatedChartOptions.series[1] = benchMarkData
    updatedChartOptions.series[2] = activeData
    setChartOptions(updatedChartOptions)
  }, [chartData])

  useEffect(() => {
    const chartHeight = layout?.isTopExpanded ? 640 : 360
    setChartOptions((prevChartOptions) => ({
      ...prevChartOptions,
      chart: {
        ...prevChartOptions.chart,
        height: chartHeight,
      },
    }))
  }, [layout])

  return (
    <HighchartsReact
      highcharts={Highcharts}
      options={chartOptions}
      containerProps={{ 'data-testid': 'chart' }}
    />
  )
}
